// deno-fmt-ignore-file
import { Guard } from '../../guard/index.mjs';
// ------------------------------------------------------------------
// Errors
// ------------------------------------------------------------------
class ValuePointerRootSetError extends Error {
    constructor(value, path, update) {
        super('Cannot set root value');
        this.value = value;
        this.path = path;
        this.update = update;
    }
}
class ValuePointerRootDeleteError extends Error {
    constructor(value, path) {
        super('Cannot delete root value');
        this.value = value;
        this.path = path;
    }
}
// ------------------------------------------------------------------
// ValuePointer
// ------------------------------------------------------------------
/** Provides functionality to update values through RFC6901 string pointers */
function Escape(component) {
    const decoded = decodeURIComponent(component);
    return decoded.replace(/~1/g, '/').replace(/~0/g, '~');
}
// ------------------------------------------------------------------
// Format
// ------------------------------------------------------------------
/** Transforms a pointer into navigable key components */
export function* Format(pointer) {
    if (pointer === '')
        return;
    let [start, end] = [0, 0];
    for (let i = 0; i < pointer.length; i++) {
        const char = pointer.charAt(i);
        if (char === '/') {
            if (i === 0) {
                start = i + 1;
            }
            else {
                end = i;
                yield Escape(pointer.slice(start, end));
                start = i + 1;
            }
        }
        else {
            end = i;
        }
    }
    yield Escape(pointer.slice(start));
}
// ------------------------------------------------------------------
// Set
// ------------------------------------------------------------------
/**
 * Sets the value at the given pointer and returns the updated value. If the pointer
 * path does not exist, the path will be created.
 */
export function Set(value, pointer, update) {
    if (pointer === '')
        throw new ValuePointerRootSetError(value, pointer, update);
    let [owner, next, key] = [null, value, ''];
    for (const component of Format(pointer)) {
        if (Guard.IsUndefined(next[component]))
            next[component] = {};
        owner = next;
        next = next[component];
        key = component;
    }
    if (Guard.IsObject(owner)) {
        owner[key] = update;
    }
    return value;
}
// ------------------------------------------------------------------
// Delete
// ------------------------------------------------------------------
/**
 * Deletes a value at the given pointer and returns the updated value.
 */
export function Delete(value, pointer) {
    if (pointer === '')
        throw new ValuePointerRootDeleteError(value, pointer);
    let [owner, next, key] = [null, value, ''];
    for (const component of Format(pointer)) {
        if (Guard.IsUndefined(next[component]) || Guard.IsNull(next[component])) {
            return value;
        }
        owner = next;
        next = next[component];
        key = component;
    }
    if (Guard.IsArray(owner)) {
        const index = parseInt(key);
        owner.splice(index, 1);
    }
    else if (Guard.IsObject(owner)) {
        delete owner[key];
    }
    return value;
}
// ------------------------------------------------------------------
// Has
// ------------------------------------------------------------------
/** Returns true if a property or element exists at the given pointer */
export function Has(value, pointer) {
    if (pointer === '')
        return true;
    let [owner, next, key] = [null, value, ''];
    for (const component of Format(pointer)) {
        if (Guard.IsUndefined(next[component]) || Guard.IsNull(next[component])) {
            return false;
        }
        owner = next;
        next = next[component];
        key = component;
    }
    return Object.getOwnPropertyNames(owner).includes(key);
}
// ------------------------------------------------------------------
// Get
// ------------------------------------------------------------------
/** Gets the value at the given pointer or undefined if not exists. */
export function Get(value, pointer) {
    if (pointer === '')
        return value;
    let current = value;
    for (const component of Format(pointer)) {
        if (current[component] === undefined)
            return undefined;
        current = current[component];
    }
    return current;
}
